<template>
  <div>
    <EditorFormPanel v-model="textOpen" :title="getEditorTranslation('text-label')" data-testid="customer-review-text">
      <div data-testid="customer-review-form">
        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel>{{ getEditorTranslation('title-label') }}</UiFormLabel>
          </div>
          <label>
            <SfInput v-model="productLegalInformation.text.title" type="text" data-testid="input-title">
              <template #suffix>
                <label for="text-title" class="rounded-lg cursor-pointer">
                  <input
                    id="text-title"
                    v-model="productLegalInformation.text.title"
                    type="text"
                    class="invisible w-8"
                  />
                </label>
              </template>
            </SfInput>
          </label>
        </div>

        <div class="py-2">
          <div class="flex justify-between mb-2">
            <UiFormLabel>{{ getEditorTranslation('link-text-label') }}</UiFormLabel>
          </div>
          <label>
            <SfInput v-model="productLegalInformation.text.linkText" type="text" data-testid="input-linkText">
              <template #suffix>
                <label for="text-linkText" class="rounded-lg cursor-pointer">
                  <input
                    id="text-linkText"
                    v-model="productLegalInformation.text.linkText"
                    type="text"
                    class="invisible w-8"
                  />
                </label>
              </template>
            </SfInput>
          </label>
        </div>
      </div>
    </EditorFormPanel>
    <EditorFormPanel
      v-model="layoutOpen"
      :title="getEditorTranslation('layout-group-label')"
      data-testid="customer-review-layout"
    >
      <EditorFullWidthToggle v-model="isFullWidth" :block-uuid="blockUuid" />

      <div class="py-2">
        <UiFormLabel>{{ getEditorTranslation('padding-label') }}</UiFormLabel>
        <div class="grid grid-cols-4 gap-px rounded-md overflow-hidden border border-gray-300">
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowUpward /></span>
            <input
              v-model.number="productLegalInformation.layout.paddingTop"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-top"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowDownward /></span>
            <input
              v-model.number="productLegalInformation.layout.paddingBottom"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-bottom"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white border-r">
            <span><SfIconArrowBack /></span>
            <input
              v-model.number="productLegalInformation.layout.paddingLeft"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-left"
            />
          </div>
          <div class="flex items-center justify-center gap-1 px-2 py-1 bg-white">
            <span><SfIconArrowForward /></span>
            <input
              v-model.number="productLegalInformation.layout.paddingRight"
              type="number"
              class="w-12 text-center outline-none"
              data-testid="padding-right"
            />
          </div>
        </div>
      </div>
    </EditorFormPanel>

    <div
      class="bg-[#FEDCA5] border border-[#BBC6BE] text-[#151A16] px-4 py-3 rounded-md mx-5 mt-1 mb-0 shadow-md shadow-[#0000000F]"
    >
      <span class="color-[#151A16]font-normal">
        {{ getEditorTranslation('legal-information-note') }}
      </span>
    </div>
  </div>
</template>

<script setup lang="ts">
import type { ProductLegalInformationProps, ProductLegalInformationContent } from './types';
import {
  SfInput,
  SfIconArrowUpward,
  SfIconArrowDownward,
  SfIconArrowBack,
  SfIconArrowForward,
} from '@storefront-ui/vue';
const textOpen = ref(true);
const layoutOpen = ref(true);

const props = defineProps<ProductLegalInformationProps>();
const { findOrDeleteBlockByUuid } = useBlockManager();
const { blockUuid } = useSiteConfiguration();
const { allBlocks: data } = useBlocks();

const productLegalInformation = computed<ProductLegalInformationContent>(() => {
  const uuid = props.meta?.uuid || blockUuid.value;
  const rawContent = findOrDeleteBlockByUuid(data.value, uuid)?.content ?? {};
  const content = rawContent as Partial<ProductLegalInformationContent>;

  if (!content.text) {
    content.text = {
      title: '',
      linkText: '',
    };
  } else {
    if (content.text.title === undefined) content.text.title = '';
    if (content.text.linkText === undefined) content.text.linkText = '';
  }

  if (!content.layout) {
    content.layout = {
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: 0,
      paddingRight: 0,
      fullWidth: false,
    };
  } else {
    if (content.layout.paddingTop === undefined) content.layout.paddingTop = 0;
    if (content.layout.paddingBottom === undefined) content.layout.paddingBottom = 0;
    if (content.layout.paddingLeft === undefined) content.layout.paddingLeft = 0;
    if (content.layout.paddingRight === undefined) content.layout.paddingRight = 0;
    if (content.layout.fullWidth === undefined) content.layout.fullWidth = false;
  }
  return content as ProductLegalInformationContent;
});

const { isFullWidth } = useFullWidthToggleForContent(productLegalInformation);
</script>

<i18n lang="json">
{
  "en": {
    "text-label": "Text",
    "title-label": "Title",
    "padding-label": "Padding",
    "layout-group-label": "Layout Settings",
    "link-text-label": "Link Text",
    "legal-information-note": "To set up the legal information displayed in this block, in your Plenty system, go to Setup » Item » Manufacturers."
  },
  "de": {
    "text-label": "Text",
    "title-label": "Titel",
    "padding-label": "Padding",
    "layout-group-label": "Layout Settings",
    "link-text-label": "Link Text",
    "legal-information-note": "To set up the legal information displayed in this block, in your Plenty system, go to Setup » Item » Manufacturers."
  }
}
</i18n>
